import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Signal, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';

import { finalize, tap } from 'rxjs/operators';

import { MarkFormAsTouchedIfInvalid } from 'tiime-expert-utils';
import {
  OverlayBase,
  TiimeOverlayModule,
  TiimeButtonModule,
  TiimeInputContainerModule,
  DatepickerHeaderComponent,
  TiimeDatepickerModule,
  TiimeIconModule,
  TiimeSnackbarService
} from 'tiime-material';

import { ErrorCode } from '@constants/error.constant';
import { DateHelper, DateInput } from '@helpers/date.helper';
import { FormHelper } from '@helpers/form.helper';
import { ErrorMessage } from '@interfaces/error-message';
import { ContractFacade } from '@modules/core/facades/contract.facade';

import { ContractUpdateNextReminderDateOverlayData } from './contract-update-next-reminder-date-overlay-data';

@Component({
  selector: 'app-contract-update-next-reminder-date-overlay',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TiimeOverlayModule,
    TiimeInputContainerModule,
    TiimeButtonModule,
    TiimeIconModule,
    TiimeDatepickerModule
  ],
  templateUrl: './contract-update-next-reminder-date-overlay.component.html',
  styleUrls: ['./contract-update-next-reminder-date-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContractUpdateNextReminderDateOverlayComponent extends OverlayBase<
  DateInput,
  ContractUpdateNextReminderDateOverlayData
> {
  readonly nextReminderDateMin: Signal<DateInput> = signal(
    DateHelper.add(DateHelper.currentDate(), 1, 'd')
  ).asReadonly();
  readonly nextReminderDateMax: Signal<DateInput> = signal(
    DateHelper.add(DateHelper.currentDate(), 15, 'd')
  ).asReadonly();
  readonly nextReminderDateCtrl: FormControl<DateInput> = new FormControl<DateInput>(
    this.overlayData().nextReminderDate,
    Validators.required
  );
  get nextReminderDateErrorMessage(): ErrorMessage {
    return FormHelper.getDatePickerErrorMessage(this.nextReminderDateCtrl, {
      checkMin: true,
      checkMax: true,
      minError: ErrorCode.INVALID,
      minErrorMessage: `La date doit être supérieure au ${DateHelper.format(DateHelper.subtract(this.nextReminderDateMin(), 1, 'd'), 'DD/MM/YYYY')}`,
      maxError: ErrorCode.INVALID,
      maxErrorMessage: `La date doit être inférieure au ${DateHelper.format(DateHelper.add(this.nextReminderDateMax(), 1, 'd'), 'DD/MM/YYYY')}`
    });
  }
  readonly tiimeDatepickerHeader = DatepickerHeaderComponent;

  constructor(
    private readonly contractFacade: ContractFacade,
    private readonly snackbar: TiimeSnackbarService
  ) {
    super();
  }

  @MarkFormAsTouchedIfInvalid('nextReminderDateCtrl')
  updateNextReminderDate(): void {
    this.isLoading = true;
    this.nextReminderDateCtrl.disable();

    const nextReminderDate = this.nextReminderDateCtrl.value;

    this.contractFacade
      .updateContractNextReminderDate(this.overlayData().contractId, nextReminderDate)
      .pipe(
        tap(() => {
          this.snackbar.openSuccess('La date de la prochaine relance a bien été modifiée');
          this.close(nextReminderDate);
        }),
        finalize(() => {
          this.isLoading = false;
          this.nextReminderDateCtrl.enable();
        })
      )
      .subscribe();
  }
}
